(function () {
  const mmMostWatchedInstances = new WeakMap();
  let mmMostWatchedActiveInstance = null;

  class MMMostWatchedVideos {
    constructor(section) {
      this.section = section;
      this.dialog = section.querySelector("[data-mm-most-watched-dialog]");
      this.player = section.querySelector("[data-mm-most-watched-player]");
      this.youtubeTarget = section.querySelector("[data-mm-most-watched-youtube]");
      this.hostedTarget = section.querySelector("[data-mm-most-watched-hosted-target]");
      this.controls = section.querySelector("[data-mm-most-watched-controls]");
      this.swiperElement = section.querySelector("[data-mm-most-watched-swiper]");
      this.pagination = section.querySelector("[data-mm-most-watched-pagination]");
      this.mobileLayout = section.dataset.mmMostWatchedMobileLayout || "grid";
      this.cardCount = Number(section.dataset.mmMostWatchedCount || 0);
      this.mobileMedia = window.matchMedia("(max-width: 950px)");
      this.swiper = null;
      this.lastTrigger = null;
      this.activeVideo = null;
      this.previousBodyOverflow = "";
      this.onClick = this.handleClick.bind(this);
      this.onCancel = this.handleCancel.bind(this);
      this.onVideoPlay = this.handleVideoPlay.bind(this);
      this.onVideoPause = this.handleVideoPause.bind(this);
      this.onVideoEnded = this.handleVideoEnded.bind(this);
      this.onBreakpointChange = this.handleBreakpointChange.bind(this);

      this.init();
    }

    init() {
      this.section.addEventListener("click", this.onClick);
      if (this.dialog) this.dialog.addEventListener("cancel", this.onCancel);
      this.addBreakpointListener();
      this.updateSwiper();
    }

    destroy() {
      this.closeModal(false);
      this.section.removeEventListener("click", this.onClick);
      if (this.dialog) this.dialog.removeEventListener("cancel", this.onCancel);
      this.removeBreakpointListener();
      this.destroySwiper();
    }

    addBreakpointListener() {
      if (typeof this.mobileMedia.addEventListener === "function") {
        this.mobileMedia.addEventListener("change", this.onBreakpointChange);
        return;
      }

      if (typeof this.mobileMedia.addListener === "function") {
        this.mobileMedia.addListener(this.onBreakpointChange);
      }
    }

    removeBreakpointListener() {
      if (typeof this.mobileMedia.removeEventListener === "function") {
        this.mobileMedia.removeEventListener("change", this.onBreakpointChange);
        return;
      }

      if (typeof this.mobileMedia.removeListener === "function") {
        this.mobileMedia.removeListener(this.onBreakpointChange);
      }
    }

    handleBreakpointChange() {
      this.updateSwiper();
    }

    updateSwiper() {
      const shouldUseSwiper =
        this.mobileLayout === "carousel" && this.cardCount > 1 && this.mobileMedia.matches && this.swiperElement;

      if (!shouldUseSwiper) {
        this.destroySwiper();
        return;
      }

      this.initSwiper();
    }

    initSwiper() {
      if (this.swiper || typeof window.Swiper !== "function") return;

      this.swiper = new window.Swiper(this.swiperElement, {
        slidesPerView: 1,
        spaceBetween: 0,
        pagination: {
          el: this.pagination,
          clickable: true,
        },
      });

      this.syncPagination();
      this.swiper.on("slideChange", () => this.syncPagination());
      this.swiper.on("transitionEnd", () => this.syncPagination());
    }

    destroySwiper() {
      if (!this.swiper) return;

      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    syncPagination() {
      if (!this.swiper || !this.pagination) return;

      const activeIndex = this.swiper.realIndex ?? this.swiper.activeIndex;
      const bullets = this.pagination.querySelectorAll(".swiper-pagination-bullet");

      bullets.forEach((bullet, index) => {
        const isActive = index === activeIndex;
        bullet.classList.toggle("swiper-pagination-bullet-active", isActive);
        bullet.setAttribute("aria-current", isActive ? "true" : "false");
      });
    }

    handleClick(event) {
      const trigger = event.target.closest("[data-mm-most-watched-trigger]");
      const close = event.target.closest("[data-mm-most-watched-close]");
      const playToggle = event.target.closest("[data-mm-most-watched-play-toggle]");
      const soundToggle = event.target.closest("[data-mm-most-watched-sound-toggle]");

      if (trigger && this.section.contains(trigger)) {
        const card = trigger.closest("[data-mm-most-watched-card]");
        this.openCard(card, trigger);
        return;
      }

      if (close && this.section.contains(close)) {
        this.closeModal();
        return;
      }

      if (playToggle && this.section.contains(playToggle)) {
        this.toggleHostedPlayback();
        return;
      }

      if (soundToggle && this.section.contains(soundToggle)) {
        this.toggleHostedSound();
        return;
      }

      if (event.target === this.dialog) {
        this.closeModal();
      }
    }

    handleCancel(event) {
      event.preventDefault();
      this.closeModal();
    }

    openCard(card, trigger) {
      if (!card || !this.dialog || !this.player) return;

      const type = card.dataset.mmVideoType;
      if (type !== "hosted" && type !== "youtube") return;

      if (mmMostWatchedActiveInstance && mmMostWatchedActiveInstance !== this) {
        mmMostWatchedActiveInstance.closeModal(false);
      }

      this.closeMedia();
      this.lastTrigger = trigger;
      mmMostWatchedActiveInstance = this;

      if (type === "hosted") {
        this.openHosted(card);
      }

      if (type === "youtube") {
        this.openYouTube(card);
      }

      this.openDialog();
    }

    openDialog() {
      // this.previousBodyOverflow = document.body.style.overflow;
      // document.body.style.overflow = "hidden";

      if (typeof this.dialog.showModal === "function") {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute("open", "");
      }
    }

    openHosted(card) {
      const template = card.querySelector("[data-mm-most-watched-hosted-template]");
      if (!template || !this.hostedTarget) return;

      const aspectRatio = this.normalizeAspectRatio(card.dataset.mmVideoAspectRatio);
      this.setPlayerAspectRatio(aspectRatio);

      const clone = template.content.cloneNode(true);
      this.hostedTarget.replaceChildren(clone);
      this.activeVideo = this.hostedTarget.querySelector("[data-mm-most-watched-hosted]");

      if (!this.activeVideo) return;

      this.activeVideo.muted = true;
      this.activeVideo.controls = false;
      this.activeVideo.playsInline = true;
      this.activeVideo.preload = "metadata";
      this.activeVideo.addEventListener("play", this.onVideoPlay);
      this.activeVideo.addEventListener("pause", this.onVideoPause);
      this.activeVideo.addEventListener("ended", this.onVideoEnded);

      if (this.controls) this.controls.hidden = false;

      this.updateHostedPlayState(false);
      this.updateHostedSoundState();

      const playPromise = this.activeVideo.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          this.updateHostedPlayState(true);
        });
      }
    }

    openYouTube(card) {
      if (!this.youtubeTarget) return;

      const youtubeInfo = this.getYouTubeInfo(card.dataset.mmYoutubeUrl || "");
      if (!youtubeInfo.id) return;

      const aspectRatio = youtubeInfo.isShort ? "9 / 16" : "16 / 9";
      this.setPlayerAspectRatio(aspectRatio);

      const iframe = document.createElement("iframe");
      iframe.className = "mm-most-watched-videos__youtube-iframe";
      iframe.src = `https://www.youtube.com/embed/${youtubeInfo.id}?autoplay=1&playsinline=1&rel=0`;
      iframe.title = `${card.dataset.mmVideoTitle || "Video"} video player`;
      iframe.allow = "autoplay; encrypted-media; picture-in-picture";
      iframe.allowFullscreen = true;

      this.youtubeTarget.replaceChildren(iframe);
      if (this.controls) this.controls.hidden = true;
    }

    closeModal(restoreFocus = true) {
      this.closeMedia();

      if (this.dialog?.open) {
        this.dialog.close();
      }

      // document.body.style.overflow = this.previousBodyOverflow;

      if (restoreFocus && this.lastTrigger) {
        this.lastTrigger.focus();
      }

      if (mmMostWatchedActiveInstance === this) {
        mmMostWatchedActiveInstance = null;
      }

      this.lastTrigger = null;
    }

    closeMedia() {
      if (this.activeVideo) {
        this.activeVideo.pause();
        this.activeVideo.removeEventListener("play", this.onVideoPlay);
        this.activeVideo.removeEventListener("pause", this.onVideoPause);
        this.activeVideo.removeEventListener("ended", this.onVideoEnded);
        this.activeVideo = null;
      }

      if (this.hostedTarget) this.hostedTarget.replaceChildren();
      if (this.youtubeTarget) this.youtubeTarget.replaceChildren();
      if (this.controls) this.controls.hidden = true;

      this.updateHostedPlayState(true);
      this.updateHostedSoundState();
    }

    toggleHostedPlayback() {
      if (!this.activeVideo) return;

      if (this.activeVideo.paused) {
        this.activeVideo.play();
      } else {
        this.activeVideo.pause();
      }
    }

    toggleHostedSound() {
      if (!this.activeVideo) return;

      this.activeVideo.muted = !this.activeVideo.muted;
      this.updateHostedSoundState();
    }

    handleVideoPlay() {
      this.updateHostedPlayState(false);
      this.updateHostedSoundState();
    }

    handleVideoPause() {
      this.updateHostedPlayState(true);
    }

    handleVideoEnded() {
      this.updateHostedPlayState(true);
    }

    updateHostedPlayState(isPaused) {
      const button = this.section.querySelector("[data-mm-most-watched-play-toggle]");
      const playIcon = this.section.querySelector("[data-mm-most-watched-play-icon]");
      const pauseIcon = this.section.querySelector("[data-mm-most-watched-pause-icon]");

      if (button) button.setAttribute("aria-label", isPaused ? "Play video" : "Pause video");
      if (playIcon) playIcon.hidden = !isPaused;
      if (pauseIcon) pauseIcon.hidden = isPaused;
    }

    updateHostedSoundState() {
      const button = this.section.querySelector("[data-mm-most-watched-sound-toggle]");
      if (!button) return;

      const isSoundOn = Boolean(this.activeVideo && !this.activeVideo.muted && this.activeVideo.volume > 0);
      button.classList.toggle("is-sound-on", isSoundOn);
      button.setAttribute("aria-label", isSoundOn ? "Turn sound off" : "Turn sound on");
      button.setAttribute("aria-pressed", String(isSoundOn));
    }

    setPlayerAspectRatio(aspectRatio) {
      if (!this.player) return;

      this.player.style.setProperty("--mm-most-watched-player-aspect-ratio", aspectRatio);
      this.player.classList.toggle("is-mm-most-watched-portrait", this.isPortraitAspectRatio(aspectRatio));
    }

    normalizeAspectRatio(value) {
      const numericValue = Number(value);

      if (Number.isFinite(numericValue) && numericValue > 0) {
        return String(numericValue);
      }

      if (typeof value === "string" && value.includes("/")) {
        return value;
      }

      return "16 / 9";
    }

    isPortraitAspectRatio(value) {
      const normalizedValue = this.normalizeAspectRatio(value);

      if (normalizedValue.includes("/")) {
        const [width, height] = normalizedValue.split("/").map((part) => Number(part.trim()));
        return Number.isFinite(width) && Number.isFinite(height) && width < height;
      }

      const numericValue = Number(normalizedValue);
      return Number.isFinite(numericValue) && numericValue < 1;
    }

    getYouTubeInfo(url) {
      if (!url) return { id: "", isShort: false };

      try {
        const parsedUrl = new URL(url);
        const pathname = parsedUrl.pathname;

        if (parsedUrl.hostname.includes("youtu.be")) {
          return { id: pathname.replace("/", "").split("/")[0], isShort: false };
        }

        if (parsedUrl.searchParams.get("v")) {
          return { id: parsedUrl.searchParams.get("v"), isShort: false };
        }

        const embedMatch = pathname.match(/\/embed\/([^/?]+)/);
        if (embedMatch) return { id: embedMatch[1], isShort: false };

        const shortsMatch = pathname.match(/\/shorts\/([^/?]+)/);
        if (shortsMatch) return { id: shortsMatch[1], isShort: true };
      } catch (error) {
        return { id: "", isShort: false };
      }

      return { id: "", isShort: false };
    }
  }

  function initMMMostWatchedVideos(container = document) {
    container.querySelectorAll("[data-mm-most-watched-section]").forEach((section) => {
      if (mmMostWatchedInstances.has(section)) return;
      mmMostWatchedInstances.set(section, new MMMostWatchedVideos(section));
    });
  }

  function unloadMMMostWatchedVideos(section) {
    const instance = mmMostWatchedInstances.get(section);
    if (!instance) return;

    instance.destroy();
    mmMostWatchedInstances.delete(section);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initMMMostWatchedVideos());
  } else {
    initMMMostWatchedVideos();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMMMostWatchedVideos(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-mm-most-watched-section]").forEach((section) => {
      unloadMMMostWatchedVideos(section);
    });
  });
})();
